import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import emailConfig from '../config/email-config';
import { theme } from '../styles/theme';

const ContactContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
`;

const ContactWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  margin-right: 50px;
  
  @media screen and (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 50px;
  }
`;

const ContactForm = styled.div`
  flex: 1;
  min-width: 300px;
`;

const ContactHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ContactText = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.6;
  margin-bottom: 32px;
`;

// Contact Info Items
const ContactInfoItems = styled.div`
  margin-top: 48px;
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const ContactIcon = styled.div`
  font-size: 1.5rem;
  color: ${theme.colors.secondary};
  margin-right: 16px;
  min-width: 24px;
`;

const ContactDetail = styled.div``;

const ContactLabel = styled.h3`
  font-size: 1.2rem;
  color: ${theme.colors.secondary};
  margin-bottom: 8px;
`;

const ContactValue = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: 1.5;
`;

// Form Elements
const Form = styled.form`
  width: 100%;
  background: ${theme.colors.primary};
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${theme.colors.secondary};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 1rem;
  color: ${theme.colors.text.primary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${theme.colors.secondary};
  border-radius: 4px;
  font-size: 1rem;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.tertiary};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${theme.colors.secondary};
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 14px 32px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    background: ${theme.colors.tertiary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
  
  &:disabled {
    background: #9e9e9e;
    cursor: not-allowed;
  }
`;

const WhatsAppButton = styled.button`
  background: #25D366;
  color: #fff;
  padding: 14px 32px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  
  &:hover {
    background: #128C7E;
    transform: translateY(-3px);
  }
`;

const FormSuccess = styled.div`
  background: #e6f7ff;
  color: #0070f3;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  text-align: center;
`;

const FormError = styled.div`
  background: #fff1f0;
  color: #ff4d4f;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 24px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 10px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState(false);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Only initialize EmailJS if a valid user ID is provided
    if (emailConfig.userId && emailConfig.userId !== "YOUR_USER_ID") {
      emailjs.init(emailConfig.userId);
    }
  }, []);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError(false);
    
    // Check if EmailJS is properly configured
    const isEmailJSConfigured = 
      emailConfig.userId && 
      emailConfig.userId !== "YOUR_USER_ID" && 
      emailConfig.serviceId && 
      emailConfig.serviceId !== "YOUR_SERVICE_ID" && 
      emailConfig.templateId && 
      emailConfig.templateId !== "YOUR_TEMPLATE_ID";
    
    if (isEmailJSConfigured) {
      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_email: emailConfig.recipientEmail
      };
      
      // Send email using EmailJS
      emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        templateParams
      )
      .then((response) => {
        console.log('Email sent successfully:', response);
        handleFormSuccess();
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        // Fall back to console logging the form data
        logFormSubmission();
        handleFormSuccess();
      });
    } else {
      // EmailJS is not configured, fall back to console logging the form data
      logFormSubmission();
      handleFormSuccess();
    }
  };
  
  // Helper function to log form submission
  const logFormSubmission = () => {
    console.log('Form submitted (EmailJS not configured):', {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      to_email: emailConfig.recipientEmail
    });
  };
  
  // Helper function to handle successful form submission
  const handleFormSuccess = () => {
    setFormSuccess(true);
    setLoading(false);
    setFormData({
      name: '',
      email: '',
      message: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setFormSuccess(false);
    }, 5000);
  };
  
  // Function to open WhatsApp with the form data
  const openWhatsApp = () => {
    const phoneNumber = '27740217708'; // Your WhatsApp number in international format (no + sign)
    const message = `Name: ${formData.name}\nEmail: ${formData.email}\nMessage: ${formData.message}`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };
  
  return (
    <ContactContainer id="contact">
      <ContactWrapper>
        <ContactInfo>
          <ContactHeading>Let's Connect</ContactHeading>
          <ContactText>
            We'd love to hear from you! Whether you're interested in our solutions, want to explore partnership opportunities, or just have a question, our team is here to help.
          </ContactText>
          
          <ContactInfoItems>
            <ContactInfoItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactDetail>
                <ContactLabel>Email</ContactLabel>
                <ContactValue>info@afrotechgroup.co.za</ContactValue>
              </ContactDetail>
            </ContactInfoItem>
            
            <ContactInfoItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactDetail>
                <ContactLabel>Phone</ContactLabel>
                <ContactValue>+27 74021 7708</ContactValue>
              </ContactDetail>
            </ContactInfoItem>
            
            <ContactInfoItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactDetail>
                <ContactLabel>Address</ContactLabel>
                
              </ContactDetail>
            </ContactInfoItem>
          </ContactInfoItems>
        </ContactInfo>
        
        <ContactForm>
          <Form onSubmit={handleSubmit}>
            {formSuccess && (
              <FormSuccess>
                Thank you for your message! We'll get back to you soon.
              </FormSuccess>
            )}
            
            {formError && (
              <FormError>
                Sorry, there was a problem sending your message. Please try again later.
              </FormError>
            )}
            
            <FormGroup>
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormInput 
                type="text" 
                id="name" 
                name="name" 
                value={formData.name}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="email">Email</FormLabel>
              <FormInput 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <FormGroup>
              <FormLabel htmlFor="message">Message</FormLabel>
              <FormTextarea 
                id="message" 
                name="message" 
                value={formData.message}
                onChange={handleChange}
                required 
              />
            </FormGroup>
            
            <ButtonGroup>
              <SubmitButton type="submit" disabled={loading}>
                Send Message {loading && <LoadingSpinner />}
              </SubmitButton>
              <WhatsAppButton 
                type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  if (formData.name && formData.message) {
                    openWhatsApp();
                  } else {
                    alert('Please fill in at least your name and message before sending to WhatsApp');
                  }
                }}
              >
                <FaWhatsapp style={{ marginRight: '8px' }} /> Send via WhatsApp
              </WhatsAppButton>
            </ButtonGroup>
          </Form>
        </ContactForm>
      </ContactWrapper>
    </ContactContainer>
  );
};

export default Contact;
